import {
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityCollection,
  type EntityIdUpdateProps,
} from "@firecms/core";
import {
  canAdminEvent,
  getEventPath,
  type EventCollectionId,
} from "../utils";
import type { AuthExtras } from "../types/auth";
import { buildRemoveUserFromEventEntityAction } from "../actions/buildRemoveUserFromEventEntityAction";
import { buildApproveStandbyEntityAction } from "../actions/approveStandby/buildApproveStandbyEntityAction";
import { buildEventUserProfileField } from "../fields/EventUserProfileField";
import { NAV_GROUP_ACCESS_MANAGEMENT } from "./navigationGroups";

export type EventMemberRole =
  | "standby"
  | "participant"
  | "operator"
  | "presenter"
  | "manager";

export type EventUser = {
  uid: string;
  role: EventMemberRole;
  standbyReason?: string;
};

export const buildEventUsersPath = (id: EventCollectionId) =>
  `${getEventPath(id)}/eventUsers`;

type EventUsersCollectionOptions = {
  id: string;
  name: string;
  singularName: string;
  description: string;
  icon: string;
  hideFromNavigation?: boolean;
  forceFilter?: EntityCollection["forceFilter"];
  allowCreate?: boolean;
  roleReadOnly?: boolean;
  includeApproveAction?: boolean;
};

const buildEventUsersCollectionBase = (
  eventId: EventCollectionId,
  options: EventUsersCollectionOptions
) => {
  const allowCreate = options.allowCreate ?? true;
  const entityActions = [
    ...(options.includeApproveAction
      ? [buildApproveStandbyEntityAction(eventId.accountId, eventId.eventId)]
      : []),
    buildRemoveUserFromEventEntityAction(eventId.accountId, eventId.eventId),
  ];

  return buildCollection<EventUser>({
    name: options.name,
    singularName: options.singularName,
    description: options.description,
    path: buildEventUsersPath(eventId),
    id: options.id,
    icon: options.icon,
    group: NAV_GROUP_ACCESS_MANAGEMENT,
    hideFromNavigation: options.hideFromNavigation,
    forceFilter: options.forceFilter,
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const canManage = canAdminEvent(eventId, extra);
      return {
        read: canManage,
        edit: canManage && allowCreate,
        create: canManage && allowCreate,
        // Use Remove from event (callable) — native delete skips team/participant cleanup.
        delete: false,
      };
    },
    properties: {
      role: buildProperty({
        name: "Role",
        description:
          "standby is temporary until slot+team; staff roles are permanent. Prefer invitations for staff.",
        dataType: "string",
        readOnly: options.roleReadOnly,
        enumValues: {
          standby: "Standby",
          participant: "Participant",
          operator: "Operator",
          presenter: "Presenter",
          manager: "Manager",
        },
      }),
      uid: buildProperty({
        name: "User ID",
        description: "Firebase Auth uid. Same as the document ID.",
        dataType: "string",
        validation: { required: true },
        // Document ID is already the uid (see onIdUpdate); hide the duplicate column.
        hideFromCollection: true,
      }),
      standbyReason: buildProperty({
        name: "Standby reason",
        description:
          "Why this person is waiting. priorJoin means this email already joined.",
        dataType: "string",
        readOnly: true,
        enumValues: {
          priorJoin: "Prior join",
          assignment: "Assignment",
          capacity: "Capacity",
        },
      }),
    },
    propertiesOrder: ["profile", "role", "standbyReason"],
    additionalFields: [
      buildEventUserProfileField(eventId.accountId, eventId.eventId),
    ],
    entityActions,
    callbacks: buildEntityCallbacks({
      onIdUpdate({ values }: EntityIdUpdateProps): string {
        return values?.uid || "Select a user";
      },
    }),
  });
};

export const buildEventUsersCollection = (
  id: EventCollectionId,
  hideFromNavigation?: boolean
) =>
  buildEventUsersCollectionBase(id, {
    id: "event_users",
    name: "Members",
    singularName: "Member",
    description:
      "Everyone who can access this session, including people waiting for approval and staff.",
    icon: "Badge",
    hideFromNavigation,
  });

export const buildStandbyEventUsersCollection = (id: EventCollectionId) =>
  buildEventUsersCollectionBase(id, {
    id: "standby_event_users",
    name: "Awaiting approval",
    singularName: "User",
    description:
      "Users who joined and are waiting to be approved into the session (slot + team).",
    icon: "PendingActions",
    forceFilter: {
      role: ["==", "standby"],
    },
    allowCreate: false,
    roleReadOnly: true,
    includeApproveAction: true,
  });
